import { Box, Typography } from '@mui/material'
import { lazy, Suspense, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TypeAnimation } from 'react-type-animation'
import { COLORS } from '@/configs'
import { getPageTitle } from '@utils/index'
import PageSEO from '@/components/SEO/PageSEO'
import RevealOnScroll from '@/components/RevealOnScroll'
import Portfolio from '@pages/Portfolio'
import About from '@pages/About'

const Service = lazy(() => import('@pages/Service'))
const Resume = lazy(() => import('@pages/Resume'))
const Contact = lazy(() => import('@pages/Contact'))

const PUBLIC_URL = process.env.PUBLIC_URL || ''
const HERO_750 = `${PUBLIC_URL}/main-img-750.webp`
const HERO_1100 = `${PUBLIC_URL}/main-img-1100.webp`
const HERO_1600 = `${PUBLIC_URL}/main-img.webp`
const TYPING_COLOR = '#FFFFFF'
const TYPING_PAUSE_MS = 800

const SectionFallback = ({
  minHeight,
}: {
  minHeight: object | number | string
}) => (
  <Box
    aria-hidden
    sx={{
      minHeight,
      width: '100%',
    }}
  />
)

const Home = () => {
  const { t, i18n } = useTranslation()

  useEffect(() => {
    document.title = getPageTitle(t('home.title'))
  }, [t])

  const typingSequence = useMemo(() => {
    const lines = t('home.typing_lines', { returnObjects: true })
    const typingLines = Array.isArray(lines)
      ? (lines as string[])
      : [t('home.introduction')]

    return typingLines.flatMap((line) => [line, TYPING_PAUSE_MS])
  }, [t])

  return (
    <>
      <PageSEO
        title={t('home.title') + ' - Minh Nguyen'}
        description={
          t('home.meta.description') ||
          "Minh Nguyen's personal portfolio showcasing web development skills, projects, and experience."
        }
        path="/"
        keywords={[
          'portfolio',
          'web developer',
          'frontend',
          'react',
          'typescript',
        ]}
      />
      <Box
        sx={{
          display: 'flex',
          position: 'relative',
          mx: { xs: -2, sm: -3, md: -4 },
          width: 'auto',
          aspectRatio: '16 / 9',
          bgcolor: 'grey.900',
          overflow: 'hidden',
        }}>
        <img
          src={HERO_1100}
          srcSet={`${HERO_750} 750w, ${HERO_1100} 1100w, ${HERO_1600} 1600w`}
          sizes="100vw"
          width={1600}
          height={900}
          alt="Background"
          fetchPriority="high"
          decoding="async"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: COLORS.BACKGROUND_HOME,
            width: '100%',
          }}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={600}
            textAlign="center"
            py={2}
            sx={{
              px: { xs: 2, sm: 4 },
              minHeight: { xs: '3.2rem', sm: '4rem', md: '4.5rem' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Quicksand, sans-serif',
              color: TYPING_COLOR,
              fontSize: { xs: '1.15rem', sm: '1.5rem', md: '2rem' },
              letterSpacing: '0.02em',
            }}>
            <TypeAnimation
              key={i18n.language}
              sequence={typingSequence}
              wrapper="span"
              speed={{ type: 'keyStrokeDelayInMs', value: 90 }}
              deletionSpeed={{ type: 'keyStrokeDelayInMs', value: 40 }}
              repeat={Infinity}
              cursor
              style={{
                display: 'inline-block',
                fontFamily: 'Quicksand, sans-serif',
                fontWeight: 600,
                color: 'inherit',
                fontSize: 'inherit',
                textAlign: 'center',
              }}
            />
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: '1440px',
          margin: '0 auto',
          width: '100%',
          px: { xs: 0, md: 0 },
        }}>
        <RevealOnScroll delay={0}>
          <Portfolio embedded />
        </RevealOnScroll>
        <RevealOnScroll delay={60}>
          <About embedded />
        </RevealOnScroll>
        <RevealOnScroll delay={80}>
          <Suspense
            fallback={<SectionFallback minHeight={{ xs: 420, sm: 480 }} />}>
            <Service showFinancial={false} embedded />
          </Suspense>
        </RevealOnScroll>
        <RevealOnScroll delay={80}>
          <Suspense
            fallback={<SectionFallback minHeight={{ xs: 720, sm: 640 }} />}>
            <Resume embedded />
          </Suspense>
        </RevealOnScroll>
        <RevealOnScroll delay={80}>
          <Suspense
            fallback={<SectionFallback minHeight={{ xs: 520, sm: 480 }} />}>
            <Contact embedded />
          </Suspense>
        </RevealOnScroll>
      </Box>
    </>
  )
}

export default Home
